/**
 * src/components/spatial/useObjectTreasury.ts
 *
 * Level 2 loading for an object's treasury: what a *pinned* card may ask
 * the chain, and what a hovered one may not.
 *
 * `spatialTooltipModel` explains the rule this file is the other half
 * of. Hovering across a city must cost nothing, so the hover card only
 * projects properties already in the browser. A click is a decision, and
 * a decision may spend a request. This hook is where that request lives.
 *
 * Once loaded, the answer is cached per object and asset for as long as
 * the map is mounted, so re-pinning the same building is free and the
 * hover card can keep showing a figure the pinned card fetched earlier.
 * A deposit invalidates it: the server forgets its own cached balance on
 * submit, and a screen still showing the pre-deposit figure would make
 * a successful contribution look like it failed.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getObjectTreasury,
  type TreasuryStatus,
} from '../../services/spatial/spatialTreasuryClient';
import type { Availability, TooltipTreasury } from './spatialTooltipModel';

/** What a treasury read is doing, for a card that has to say so. */
export type TreasuryPhase = 'idle' | 'loading' | 'ready' | 'failed';

export interface TreasuryState {
  phase: TreasuryPhase;
  status: TreasuryStatus | null;
  reason: string | null;
}

export const IDLE_TREASURY: TreasuryState = { phase: 'idle', status: null, reason: null };

/**
 * What an object's treasury is cached under.
 *
 * The asset is part of the key because the vault is asset-agnostic: two
 * assets in one object are two balances, and collapsing them would show
 * a visitor somebody else's currency.
 */
export function treasuryCacheKey(objectId: string, asset?: string | null): string {
  return `${objectId}|${asset || 'native'}`;
}

/**
 * Fold a completed read into the state a card renders.
 *
 * Pure, and separate from the hook, because the interesting rule is not
 * about React: a failed refresh must not blank a figure that was true a
 * moment ago. "2.5 XLM, could not refresh" is more use to somebody
 * deciding whether to contribute than an empty row.
 */
export function applyTreasuryResult(
  prev: TreasuryState,
  result: { ok: true; [key: string]: unknown } | { ok: false; reason: string },
): TreasuryState {
  if (!result.ok) {
    return { phase: 'failed', status: prev.status, reason: result.reason };
  }
  return { phase: 'ready', status: result as unknown as TreasuryStatus, reason: null };
}

/**
 * Translate a chain read into the three-valued shape the hover card
 * speaks.
 *
 * Pure, and exported for its own tests: the distinction between "cannot
 * receive" and "nobody has looked yet" is the whole reason the tooltip's
 * availability type has three values, and it is worth pinning down.
 */
export function toTooltipTreasury(status: TreasuryStatus | null): TooltipTreasury | null {
  if (!status) return null;

  const canReceive: Availability = status.canReceive ? 'available' : 'unavailable';

  return {
    balance: status.balance
      ? {
          asset: status.balance.asset?.address || status.asset?.address || '',
          assetCode: status.balance.asset?.code || status.asset?.code || null,
          amount: status.balance.amount,
        }
      : null,
    canReceive,
    // One word, deliberately. Who may withdraw from a building is not
    // something a hover card should tell every passer-by.
    hasBeneficiary: !!status.beneficiary,
    reason: status.blocker,
  };
}

/**
 * Treasury state for the pinned object, and a cache shared with hover.
 *
 * `objectId` null means nothing is pinned, which is the resting state
 * and costs nothing.
 */
export function useObjectTreasury(objectId: string | null, asset?: string | null) {
  const [state, setState] = useState<TreasuryState>(IDLE_TREASURY);
  // Survives re-pinning and re-renders for the life of the map.
  const cache = useRef<Map<string, TreasuryStatus>>(new Map());
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const key = objectId ? treasuryCacheKey(objectId, asset) : null;

  const load = useCallback(async (force = false) => {
    if (!objectId || !key) return;

    const cached = cache.current.get(key);
    if (cached && !force) {
      setState({ phase: 'ready', status: cached, reason: null });
      return;
    }

    setState((prev) => ({ ...prev, phase: 'loading' }));
    const result = await getObjectTreasury(objectId, asset);
    if (!mounted.current) return;

    // A failure is deliberately not cached, so the next pin tries again.
    if (result.ok) cache.current.set(key, result as unknown as TreasuryStatus);
    setState((prev) => applyTreasuryResult(prev, result as Parameters<typeof applyTreasuryResult>[1]));
  }, [objectId, key, asset]);

  // Pinning is the trigger. Nothing else in this file starts a request.
  useEffect(() => {
    if (!objectId) {
      setState(IDLE_TREASURY);
      return;
    }
    void load();
  }, [objectId, load]);

  /** Forget and re-read: what a landed deposit calls. */
  const refresh = useCallback(async () => {
    if (key) cache.current.delete(key);
    await load(true);
  }, [key, load]);

  /**
   * Whatever was last loaded for an object, for the hover card to show.
   *
   * A read, never a trigger: asking about an object nobody has pinned
   * returns null rather than starting a request, which is the property
   * that keeps dragging across a city free.
   */
  const cachedFor = useCallback((id: string | null): TreasuryStatus | null => {
    if (!id) return null;
    return cache.current.get(treasuryCacheKey(id, asset)) ?? null;
  }, [asset]);

  return { ...state, refresh, cachedFor };
}
